import React, { useContext, useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { makeStyles, withStyles } from '@material-ui/core/styles';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Grid,
    IconButton,
    InputAdornment,
    Popover,
    Box,
    Typography,
    Badge,
    FormHelperText,
    Tooltip,
    CircularProgress,
    Button
} from '@material-ui/core';
import RemoveCircleOutlineIcon from '@material-ui/icons/RemoveCircleOutline';
import AddIcon from '@material-ui/icons/Add';
import SearchIcon from '@material-ui/icons/Search';
import PhotoCameraIcon from '@material-ui/icons/PhotoCamera';
import DeviceHubIcon from '@material-ui/icons/DeviceHub';
import EditIcon from '@material-ui/icons/Edit';
import LinkIcon from '@material-ui/icons/Link';
import moment from 'moment';

import AppContext from '../../../../App/AppContext';
import { SecondaryButton, SecondaryCheckbox, SelectBox, TextBox } from '../../../../Core/FormInput';
import { CustomIconButton } from '../../../../Core/FormInput/AppButton';
import DialogComp from '../../../../Core/Modal/dialogModal';
import { getPartListByPartNumber } from '../../../../Core/Service/partRequisitions-service';
import PartReqSupplierPhotoModal from '../../../internal/part_req_supplier/ImageUploader';

const ordinalNumbers = [
    '1st', '2nd', '3rd', '4th', '5th', '6th', '7th', '8th', '9th', '10th',
    '11th', '12th', '13th', '14th', '15th', '16th', '17th', '18th', '19th', '20th'
];

const useStyles = makeStyles((theme) => ({
    root: {
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        width: '100%',
        minHeight: 0,
        flex: 1,
        overflow: 'hidden'
    },
    tableContainer: {
        flex: 1,
        minHeight: 0,
        height: '100%',
        overflowY: 'auto',
        border: '1px solid #e2e8f0',
        borderRadius: 6,
        backgroundColor: '#fff',
        '&::-webkit-scrollbar': {
            width: 8,
            height: 8
        },
        '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(0, 0, 0, 0.2)',
            borderRadius: 4
        }
    },
    table: {
        minWidth: 900,
        borderCollapse: 'separate'
    },
    headerCell: {
        fontSize: '0.85rem',
        fontWeight: 700,
        backgroundColor: '#f8fafc',
        color: '#334155',
        padding: '10px 12px',
        borderBottom: '2px solid #e2e8f0'
    },
    tableRow: {
        transition: 'background-color 0.15s ease',
        '&:hover': {
            backgroundColor: '#f8fafc !important'
        }
    },
    tableCell: {
        fontSize: '0.875rem',
        padding: '6px 10px',
        verticalAlign: 'middle',
        borderBottom: '1px solid #f1f5f9'
    },
    addButton: {
        backgroundColor: '#10b981',
        color: '#ffffff',
        width: 28,
        height: 28,
        borderRadius: 6,
        padding: 0,
        transition: 'all 0.2s',
        '&:hover': {
            backgroundColor: '#059669'
        },
        '&.Mui-disabled': {
            backgroundColor: '#cbd5e1',
            color: '#94a3b8'
        }
    },
    removeButton: {
        color: '#ef4444',
        padding: 4,
        '&:hover': {
            backgroundColor: 'rgba(239, 68, 68, 0.08)'
        },
        '&.Mui-disabled': {
            color: '#cbd5e1'
        }
    },
    inputNumber: {
        '& input[type=number]': {
            '-moz-appearance': 'textfield'
        },
        '& input[type=number]::-webkit-outer-spin-button': {
            '-webkit-appearance': 'none',
            margin: 0
        },
        '& input[type=number]::-webkit-inner-spin-button': {
            '-webkit-appearance': 'none',
            margin: 0
        }
    },
    nestedRowContainer: {
        backgroundColor: '#f8fafc',
        padding: '10px 16px',
        borderLeft: '4px solid #3b82f6',
        borderRadius: '0 6px 6px 0',
        margin: '4px 0'
    },
    nestedTable: {
        minWidth: 800,
        backgroundColor: '#fff',
        borderRadius: 4,
        border: '1px solid #e2e8f0'
    },
    nestedHeaderCell: {
        fontSize: '0.75rem',
        fontWeight: 700,
        backgroundColor: '#f1f5f9',
        color: '#475569',
        padding: '6px 10px',
        borderBottom: '1px solid #e2e8f0'
    },
    nestedCell: {
        fontSize: '0.8rem',
        padding: '6px 10px',
        borderBottom: '1px solid #f1f5f9'
    },
    linkBar: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        backgroundColor: '#eff6ff',
        border: '1px solid #bfdbfe',
        borderRadius: 6,
        padding: '6px 14px',
        marginBottom: 10,
        cursor: 'pointer',
        transition: 'all 0.2s',
        '&:hover': {
            backgroundColor: '#dbeafe'
        }
    },
    footerActions: {
        display: 'flex',
        gap: 16,
        marginTop: 14,
        flexShrink: 0
    },
    centerBox: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: 180,
        height: '100%',
        gap: 10
    }
}));

const organizeByParent = (input) => {
    const activeItems = (input || []).filter((item) => !item.isDeleted);
    const parentMap = {};

    activeItems.forEach((item, idx) => {
        const lineId = item.lineID !== undefined && item.lineID !== null ? item.lineID : (item.partReqID || `item_${idx}`);
        if (item.parentLineID === null || item.parentLineID === undefined) {
            parentMap[lineId] = { ...item, lineID: lineId, children: [] };
        }
    });

    activeItems.forEach((item, idx) => {
        const lineId = item.lineID !== undefined && item.lineID !== null ? item.lineID : (item.partReqID || `item_${idx}`);
        if (item.parentLineID !== null && item.parentLineID !== undefined) {
            if (parentMap[item.parentLineID]) {
                parentMap[item.parentLineID].children.push({ ...item, lineID: lineId });
            } else {
                parentMap[lineId] = { ...item, lineID: lineId, children: [] };
            }
        }
    });

    return Object.values(parentMap);
};

const MatchingPartList = ({ list, loading, handleRowSelect }) => {
    return (
        <Paper style={{ borderRadius: 6, maxHeight: 320, overflow: 'auto', width: 380, boxShadow: '0 4px 16px rgba(0,0,0,0.12)' }}>
            <TableContainer>
                <Table size="small" stickyHeader>
                    <TableHead>
                        <TableRow>
                            <TableCell style={{ fontSize: 12, fontWeight: 700, backgroundColor: '#f1f5f9' }}>Part No</TableCell>
                            <TableCell style={{ fontSize: 12, fontWeight: 700, backgroundColor: '#f1f5f9' }}>Description</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <TableRow>
                                <TableCell colSpan={2} align="center" style={{ padding: 20 }}>
                                    <CircularProgress size={24} />
                                    <Typography variant="caption" display="block" style={{ marginTop: 6 }}>Searching parts...</Typography>
                                </TableCell>
                            </TableRow>
                        ) : list?.length > 0 ? (
                            list.map((j) => (
                                <TableRow
                                    key={j.partNumber + j.description}
                                    hover
                                    style={{ cursor: 'pointer' }}
                                    onClick={() => handleRowSelect(j)}
                                >
                                    <TableCell style={{ fontSize: 12, fontWeight: 600, color: '#1e40af' }}>{j.partNumber}</TableCell>
                                    <TableCell style={{ fontSize: 12 }}>{j.description}</TableCell>
                                </TableRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell colSpan={2} align="center" style={{ padding: 16, color: '#64748b' }}>
                                    No matching parts found.
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        </Paper>
    );
};

const ChildAssociatedRowValue = ({ handleChildAssocate, selectedAssociatedSubData = {}, screenDropdown = {}, onCancel }) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const [anchorE2, setAnchorE2] = useState(null);
    const [matchingPartNumber, setMatchingPartNumber] = useState([]);
    const [loading, setLoading] = useState(false);
    const QtyNewRef = useRef(null);

    const [state, setState] = useState({
        partNumber: '',
        partDescription: '',
        partQty: '',
        isFitted: false,
        isReturnToPartsDepo: false,
        partReqStatus: '',
        isImageErrorMessage: ''
    });

    const isUpdate = Boolean(selectedAssociatedSubData.lineID);

    useEffect(() => {
        if (selectedAssociatedSubData.lineID) {
            setState({
                partNumber: selectedAssociatedSubData.partNumber || '',
                partDescription: selectedAssociatedSubData.partDescription || '',
                partQty: selectedAssociatedSubData.partQty || '',
                isFitted: selectedAssociatedSubData.isFitted || false,
                isReturnToPartsDepo: selectedAssociatedSubData.isReturnToPartsDepo || false,
                partReqStatus: selectedAssociatedSubData.partReqStatus || '',
                isImageErrorMessage: selectedAssociatedSubData.isImageErrorMessage || ''
            });
        }
    }, [selectedAssociatedSubData]);

    const handleClosePOP = () => {
        setAnchorEl(null);
        setAnchorE2(null);
    };

    const handleRowSelect = (params) => {
        setState((st) => ({
            ...st,
            partNumber: params.partNumber,
            partDescription: params.description
        }));
        handleClosePOP();
    };

    const handleInputOnChange = (e) => {
        const { name, value, checked } = e.target;
        setState((st) => {
            const newSt = { ...st };
            if (name === 'isFitted' || name === 'isReturnToPartsDepo') {
                newSt[name] = checked;
            } else if (name === 'partReqStatus') {
                const isPhotoREQ = screenDropdown.partReqStatus?.find((k) => value === k.id)?.photoRequired;
                if (isPhotoREQ) {
                    newSt['isImageErrorMessage'] = 'Image is Required';
                } else {
                    newSt['isImageErrorMessage'] = '';
                }
                newSt[name] = value;
            } else {
                newSt[name] = value;
            }
            return newSt;
        });
    };

    const onCloseWrapper = () => {
        handleChildAssocate(isUpdate, {
            ...selectedAssociatedSubData,
            partNumber: state.partNumber,
            partDescription: state.partDescription,
            partQty: state.partQty,
            isFitted: state.isFitted,
            isReturnToPartsDepo: state.isReturnToPartsDepo,
            partReqStatus: state.partReqStatus,
            isImageErrorMessage: state.isImageErrorMessage
        });
    };

    const pullDataByPartNumber = (val, type) => async (event) => {
        setLoading(true);
        const anchorTarget = event.currentTarget.closest('.MuiFormControl-root') || event.currentTarget.closest('.MuiGrid-item') || event.currentTarget;
        if (type === 'part') {
            setAnchorEl(anchorTarget);
            const res = await getPartListByPartNumber(val, null);
            if (res?.success) {
                setMatchingPartNumber(res.data?.partDetails || []);
            }
        } else {
            setAnchorE2(anchorTarget);
            const res = await getPartListByPartNumber(null, val);
            if (res?.success) {
                setMatchingPartNumber(res.data?.partDetails || []);
            }
        }
        setLoading(false);
    };

    return (
        <Box py={1}>
            <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={6}>
                    <Typography variant="caption" style={{ fontWeight: 600, color: '#334155', display: 'block', marginBottom: 2 }}>
                        Description
                    </Typography>
                    <TextBox
                        size="small"
                        margin="dense"
                        name="partDescription"
                        onChange={handleInputOnChange}
                        value={state.partDescription}
                        autoFocus={true}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <CustomIconButton
                                        tabIndex={-1}
                                        onClick={pullDataByPartNumber(state.partDescription, 'desc')}
                                    >
                                        <SearchIcon fontSize="small" />
                                    </CustomIconButton>
                                </InputAdornment>
                            )
                        }}
                    />
                    <Popover
                        open={Boolean(anchorE2)}
                        anchorEl={anchorE2}
                        onClose={handleClosePOP}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                    >
                        <MatchingPartList list={matchingPartNumber} loading={loading} handleRowSelect={handleRowSelect} />
                    </Popover>
                </Grid>

                <Grid item xs={12} sm={6}>
                    <Typography variant="caption" style={{ fontWeight: 600, color: '#334155', display: 'block', marginBottom: 2 }}>
                        Part No.
                    </Typography>
                    <TextBox
                        size="small"
                        margin="dense"
                        name="partNumber"
                        value={state.partNumber}
                        onChange={handleInputOnChange}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <CustomIconButton tabIndex={-1} onClick={pullDataByPartNumber(state.partNumber, 'part')}>
                                        <SearchIcon fontSize="small" />
                                    </CustomIconButton>
                                </InputAdornment>
                            )
                        }}
                    />
                    <Popover
                        open={Boolean(anchorEl)}
                        anchorEl={anchorEl}
                        onClose={handleClosePOP}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                    >
                        <MatchingPartList list={matchingPartNumber} loading={loading} handleRowSelect={handleRowSelect} />
                    </Popover>
                </Grid>

                <Grid item xs={12} sm={6}>
                    <Typography variant="caption" style={{ fontWeight: 600, color: '#334155', display: 'block', marginBottom: 2 }}>
                        Quantity
                    </Typography>
                    <TextBox
                        size="small"
                        margin="dense"
                        name="partQty"
                        type="number"
                        onChange={handleInputOnChange}
                        value={state.partQty}
                        inputRef={QtyNewRef}
                    />
                </Grid>

                <Grid item xs={12} sm={6}>
                    <Typography variant="caption" style={{ fontWeight: 600, color: '#334155', display: 'block', marginBottom: 2 }}>
                        Status
                    </Typography>
                    <SelectBox
                        size="small"
                        margin="dense"
                        List={screenDropdown.partReqStatus || []}
                        name="partReqStatus"
                        onChange={handleInputOnChange}
                        value={state.partReqStatus}
                    />
                </Grid>

                <Grid item xs={12}>
                    <Box mt={2}>
                        <Grid container spacing={2}>
                            <Grid item xs={6}>
                                <SecondaryButton
                                    fullWidth
                                    onClick={onCloseWrapper}
                                    disabled={!state.partNumber}
                                >
                                    {isUpdate ? 'Update Sub-Part' : 'Add Sub-Part'}
                                </SecondaryButton>
                            </Grid>
                            <Grid item xs={6}>
                                <SecondaryButton
                                    fullWidth
                                    onClick={onCancel}
                                >
                                    Cancel
                                </SecondaryButton>
                            </Grid>
                        </Grid>
                    </Box>
                </Grid>
            </Grid>
        </Box>
    );
};

export default function AssociatedMaterial({
    list = [],
    handleAssociatedRow,
    removeAssociatedRow,
    handleAssociateInput,
    onClose,
    isReadOnly,
    setInputList,
    modalIndex,
    screenDropdown = {},
    userlinkList = [],
    handleLinkRows,
    selectedWriteUp = {}
}) {
    const { showToast, userRoles } = useContext(AppContext);
    const classes = useStyles();
    const isTechnician = userRoles.includes('technician');

    const Qtyref = useRef(null);

    const [state, setState] = useState({
        matchingPartNumber: [],
        loading: false,
        showAddParts: false,
        showImage: false,
        partId: null,
        desc: '',
        showLinkTable: false,
        selectedAssociatedId: null,
        selectedAssociatedSubData: null,
        partDateCreated: null,
        partIndex: null
    });

    const [anchorEl, setAnchorEl] = useState(null);
    const [anchorE2, setAnchorE2] = useState(null);

    const descInputRefs = useRef({});
    const prevLineIdsRef = useRef(new Set((list || []).map((q) => q.lineID)));

    const sortedList = useMemo(() => {
        return list?.length > 0 ? organizeByParent([...list]) : [];
    }, [list]);

    useEffect(() => {
        const currentLineIds = (list || []).map((q) => q.lineID);
        const newLineId = currentLineIds.find((id) => id !== undefined && !prevLineIdsRef.current.has(id));

        if (newLineId !== undefined) {
            setTimeout(() => {
                const inputEl = descInputRefs.current[newLineId];
                if (inputEl) {
                    inputEl.focus();
                }
            }, 50);
        }

        prevLineIdsRef.current = new Set(currentLineIds);
    }, [list]);

    const sortedPartToLinkList = useMemo(() => {
        if (!userlinkList?.length) return [];
        return userlinkList.filter((q) => {
            if (
                q.isLinked &&
                (selectedWriteUp.dateCreated === q.dateCreated ||
                    (selectedWriteUp.technicianNotesItemID === q.technicianNotesItemID && q.technicianNotesItemID != null))
            ) {
                return true;
            }
            if (!q.dateCreated && !q.technicianNotesItemID) {
                return true;
            }
            return false;
        });
    }, [userlinkList, selectedWriteUp]);

    const totalSelectedPartToLinkCount = useMemo(() => {
        return sortedPartToLinkList.filter((a) => a.isLinked).length;
    }, [sortedPartToLinkList]);

    const handleClosePOP = () => {
        setAnchorEl(null);
        setAnchorE2(null);
    };

    const handleRowSelect = (params) => {
        if (state.selectedAssociatedId) {
            setInputList((st) => {
                const newSt = [...st];
                const updatedNestedArray = [...st[modalIndex].associatedMaterials];
                const objIndex = updatedNestedArray.findIndex((q) => q.lineID === state.selectedAssociatedId);
                if (objIndex !== -1) {
                    updatedNestedArray[objIndex].partDescription = params.description;
                    updatedNestedArray[objIndex].partNumber = params.partNumber;
                    updatedNestedArray[objIndex].technicianNotesItemID = newSt[modalIndex].technicianNotesItemID || null;
                    updatedNestedArray[objIndex].dateCreated = newSt[modalIndex].technicianNotesItemID
                        ? moment().format('YYYY-MM-DDTHH:mm:ss.SSS')
                        : newSt[modalIndex].dateCreated;
                }
                newSt[modalIndex].associatedMaterials = updatedNestedArray;
                return newSt;
            });
        }

        setState((st) => ({
            ...st,
            loading: false,
            matchingPartNumber: [],
            selectedAssociatedId: null
        }));

        handleClosePOP();
    };

    const pullDataByPartNumber = (val, type, selectedPartId, index) => async (event) => {
        setState((st) => ({
            ...st,
            loading: true,
            selectedAssociatedId: selectedPartId,
            partIndex: index
        }));

        const anchorTarget = event.currentTarget.closest('.MuiFormControl-root') || event.currentTarget.closest('td') || event.currentTarget;

        if (type === 'part') {
            setAnchorEl(anchorTarget);
            const res = await getPartListByPartNumber(val, null);
            if (res?.success) {
                setState((st) => ({
                    ...st,
                    matchingPartNumber: res.data?.partDetails || [],
                    loading: false
                }));
            } else {
                setState((st) => ({ ...st, loading: false }));
            }
        } else {
            setAnchorE2(anchorTarget);
            const res = await getPartListByPartNumber(null, val);
            if (res?.success) {
                setState((st) => ({
                    ...st,
                    matchingPartNumber: res.data?.partDetails || [],
                    loading: false
                }));
            } else {
                setState((st) => ({ ...st, loading: false }));
            }
        }
    };

    const handleSave = () => {
        if (list?.length > 0) {
            const index = list.findIndex((k) => k.partNumber === '' && k.partReqStatus !== 1);
            if (index > -1) {
                showToast(`${ordinalNumbers[index] || index + 1} row cannot be empty. Please fill in the required information.`);
                return;
            }
        }
        onClose();
    };

    const handleSubPartModal = (id) => {
        setState((st) => ({
            ...st,
            showAddParts: true,
            selectedAssociatedId: id,
            selectedAssociatedSubData: {}
        }));
    };

    const handleEditSubPart = (subPartData) => {
        setState((st) => ({
            ...st,
            showAddParts: true,
            selectedAssociatedSubData: subPartData
        }));
    };

    const handleCloseSubPartModal = () => {
        setState((st) => ({
            ...st,
            showAddParts: false,
            selectedAssociatedSubData: null,
            selectedAssociatedId: null
        }));
    };

    const handleChildAssocate = (isUpdate, data) => {
        if (isUpdate) {
            setInputList((st) => {
                const newSt = [...st];
                const updatedNestedArray = [...st[modalIndex].associatedMaterials];
                const objIndex = updatedNestedArray.findIndex((q) => q.lineID === data.lineID);
                if (objIndex !== -1) {
                    updatedNestedArray[objIndex] = { ...updatedNestedArray[objIndex], ...data };
                    updatedNestedArray[objIndex].dateCreated = newSt[modalIndex].technicianNotesItemID
                        ? moment().format('YYYY-MM-DDTHH:mm:ss.SSS')
                        : newSt[modalIndex].dateCreated;
                }
                newSt[modalIndex].associatedMaterials = updatedNestedArray;
                return newSt;
            });
        } else {
            setInputList((st) => {
                const newSt = [...st];
                const updatedNestedArray = [...st[modalIndex].associatedMaterials];
                const isParentAssoData = updatedNestedArray.find((q) => q.lineID === state.selectedAssociatedId) || {};
                updatedNestedArray.unshift({
                    partReqID: null,
                    technicianNotesItemID: newSt[modalIndex].technicianNotesItemID || null,
                    partNumber: data.partNumber || '',
                    partDescription: data.partDescription || '',
                    partReqStatus: data.partReqStatus || '',
                    partQty: data.partQty || '',
                    isDeleted: false,
                    isFitted: isParentAssoData.isFitted || false,
                    isReturnToPartsDepo: isParentAssoData.isReturnToPartsDepo || false,
                    dateCreated: newSt[modalIndex].technicianNotesItemID ? moment().format('YYYY-MM-DDTHH:mm:ss.SSS') : newSt[modalIndex].dateCreated,
                    lineID: 50 + updatedNestedArray.length + 2,
                    parentLineID: state.selectedAssociatedId,
                    isImageErrorMessage: data.isImageErrorMessage,
                    initialPartReqStatus: null
                });
                newSt[modalIndex].associatedMaterials = updatedNestedArray;
                return newSt;
            });
        }
        handleCloseSubPartModal();
    };

    const handleImage = (row) => () => {
        setState((st) => ({
            ...st,
            partId: row.partReqID,
            desc: row.partDescription,
            showImage: true,
            partDateCreated: row.dateCreated,
            selectedAssociatedId: row.lineID
        }));
    };

    const onImagemodalClose = (res) => {
        if (res && state.selectedAssociatedId) {
            setInputList((st) => {
                const newSt = [...st];
                const updatedNestedArray = [...st[modalIndex].associatedMaterials];
                const objIndex = updatedNestedArray.findIndex((q) => q.lineID === state.selectedAssociatedId);
                if (objIndex !== -1) {
                    delete updatedNestedArray[objIndex]['isImageErrorMessage'];
                }
                newSt[modalIndex].associatedMaterials = updatedNestedArray;
                return newSt;
            });
        }
        setState((st) => ({ ...st, showImage: false, partId: null, desc: '', selectedAssociatedId: null }));
    };

    const handleLinkTable = () => {
        setState((st) => ({ ...st, showLinkTable: !st.showLinkTable }));
    };

    return (
        <Box className={classes.root}>
            {/* Parts to Link Toggle Banner */}
            {sortedPartToLinkList.length > 0 && (
                <Box className={classes.linkBar} onClick={handleLinkTable}>
                    <Box display="flex" alignItems="center" gap={1}>
                        <LinkIcon style={{ color: '#2563eb', transform: 'rotate(90deg)' }} fontSize="small" />
                        <Typography variant="body2" style={{ fontWeight: 600, color: '#1e40af' }}>
                            {state.showLinkTable ? 'Hide Linkable Parts' : 'Click to View & Link New Parts'}
                        </Typography>
                    </Box>
                    <Typography variant="caption" style={{ fontWeight: 700, color: '#1e40af' }}>
                        Linked: {totalSelectedPartToLinkCount} / {sortedPartToLinkList.length}
                    </Typography>
                </Box>
            )}

            {/* Parts to Link Table */}
            {sortedPartToLinkList.length > 0 && state.showLinkTable && (
                <Box mb={2} style={{ maxHeight: 180, overflowY: 'auto', border: '1px solid #cbd5e1', borderRadius: 6 }}>
                    <Table size="small" stickyHeader aria-label="Linkable Parts Table">
                        <TableHead>
                            <TableRow>
                                <TableCell style={{ width: '5%', backgroundColor: '#f1f5f9', fontWeight: 700 }}></TableCell>
                                <TableCell style={{ width: '30%', backgroundColor: '#f1f5f9', fontWeight: 700 }}>Description</TableCell>
                                <TableCell style={{ width: '25%', backgroundColor: '#f1f5f9', fontWeight: 700 }}>Part No.</TableCell>
                                <TableCell style={{ width: '10%', backgroundColor: '#f1f5f9', fontWeight: 700 }}>Quantity</TableCell>
                                <TableCell style={{ width: '18%', backgroundColor: '#f1f5f9', fontWeight: 700 }}>Status</TableCell>
                                <TableCell style={{ backgroundColor: '#f1f5f9', fontWeight: 700 }}>Returned?</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {sortedPartToLinkList.map((value) => {
                                const storedStatusVal = screenDropdown.partReqStatus?.find((k) => k.id === value.partReqStatus)?.name || 'N/A';
                                return (
                                    <TableRow key={value.partReqID || value.lineID} hover>
                                        <TableCell align="center">
                                            <input
                                                type="checkbox"
                                                onChange={(e) => handleLinkRows(e, value)}
                                                checked={Boolean(value.isLinked)}
                                                style={{ cursor: 'pointer' }}
                                            />
                                        </TableCell>
                                        <TableCell>{value.partDescription || '-'}</TableCell>
                                        <TableCell>{value.partNumber || '-'}</TableCell>
                                        <TableCell>{value.partQty || 0}</TableCell>
                                        <TableCell>{storedStatusVal}</TableCell>
                                        <TableCell>{value.isReturnToPartsDepo ? 'Yes' : 'No'}</TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </Box>
            )}

            {/* Main Associated Materials Table Container */}
            <TableContainer className={classes.tableContainer}>
                <Table className={classes.table} size="small" stickyHeader aria-label="Associated Materials Table">
                    <TableHead>
                        <TableRow>
                            <TableCell className={classes.headerCell} style={{ width: '5%' }} align="center">
                                <Tooltip title={isReadOnly ? '' : 'Add Associated Part'}>
                                    <span>
                                        <IconButton
                                            className={classes.addButton}
                                            onClick={handleAssociatedRow}
                                            disabled={isReadOnly}
                                            size="small"
                                        >
                                            <AddIcon fontSize="small" />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                            </TableCell>
                            <TableCell className={classes.headerCell} style={{ width: '34%' }}>Description</TableCell>
                            <TableCell className={classes.headerCell} style={{ width: '24%' }}>Part No.</TableCell>
                            <TableCell className={classes.headerCell} style={{ width: '9%' }}>Quantity</TableCell>
                            <TableCell className={classes.headerCell} style={{ width: '13%' }}>Status</TableCell>
                            <TableCell className={classes.headerCell} style={{ width: '9%' }}>Returned?</TableCell>
                            <TableCell className={classes.headerCell} style={{ width: '6%' }} align="right"></TableCell>
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {sortedList.length > 0 ? (
                            sortedList.map((row, index) => {
                                const isRowFittedFromApi = row.initialPartReqStatus !== undefined
                                    ? row.initialPartReqStatus === 15
                                    : Boolean(row.partReqID && row.partReqStatus === 15);

                                return !row.isDeleted ? (
                                    <React.Fragment key={row.lineID || row.name || index}>
                                        <TableRow className={classes.tableRow}>
                                            {/* Action / Delete Button */}
                                            <TableCell className={classes.tableCell} align="center">
                                                <Tooltip title={isReadOnly || (isTechnician && isRowFittedFromApi) ? '' : 'Remove Part'}>
                                                    <span>
                                                        <IconButton
                                                            className={classes.removeButton}
                                                            size="small"
                                                            onClick={() => removeAssociatedRow(row.lineID)}
                                                            disabled={isReadOnly || (isTechnician && isRowFittedFromApi)}
                                                        >
                                                            <RemoveCircleOutlineIcon fontSize="small" />
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>
                                            </TableCell>

                                            {/* Part Description */}
                                            <TableCell className={classes.tableCell}>
                                                <TextBox
                                                    size="small"
                                                    margin="dense"
                                                    name="partDescription"
                                                    value={row.partDescription || ''}
                                                    inputRef={(el) => {
                                                        if (el) {
                                                            descInputRefs.current[row.lineID] = el;
                                                        } else {
                                                            delete descInputRefs.current[row.lineID];
                                                        }
                                                    }}
                                                    onChange={(e) => handleAssociateInput(e, row.lineID)}
                                                    disabled={isReadOnly || (isTechnician && isRowFittedFromApi)}
                                                    InputProps={{
                                                        endAdornment: (
                                                            <InputAdornment position="end">
                                                                <CustomIconButton
                                                                    tabIndex={-1}
                                                                    onClick={pullDataByPartNumber(row.partDescription, 'desc', row.lineID, index)}
                                                                    disabled={isReadOnly || (isTechnician && isRowFittedFromApi)}
                                                                >
                                                                    <SearchIcon fontSize="small" />
                                                                </CustomIconButton>
                                                            </InputAdornment>
                                                        )
                                                    }}
                                                />
                                            </TableCell>

                                            {/* Part Number */}
                                            <TableCell className={classes.tableCell}>
                                                <TextBox
                                                    size="small"
                                                    margin="dense"
                                                    name="partNumber"
                                                    disabled={isReadOnly || (isTechnician && isRowFittedFromApi)}
                                                    value={row.partNumber || ''}
                                                    onChange={(e) => handleAssociateInput(e, row.lineID)}
                                                    InputProps={{
                                                        endAdornment: (
                                                            <InputAdornment position="end">
                                                                <CustomIconButton
                                                                    tabIndex={-1}
                                                                    disabled={isReadOnly || (isTechnician && isRowFittedFromApi)}
                                                                    onClick={pullDataByPartNumber(row.partNumber, 'part', row.lineID, index)}
                                                                >
                                                                    <SearchIcon fontSize="small" />
                                                                </CustomIconButton>
                                                            </InputAdornment>
                                                        )
                                                    }}
                                                />
                                            </TableCell>

                                            {/* Quantity */}
                                            <TableCell className={classes.tableCell}>
                                                <TextBox
                                                    size="small"
                                                    margin="dense"
                                                    disabled={isReadOnly || (isTechnician && isRowFittedFromApi)}
                                                    className={classes.inputNumber}
                                                    name="partQty"
                                                    type="number"
                                                    value={row.partQty}
                                                    onChange={(e) => handleAssociateInput(e, row.lineID)}
                                                    inputRef={state.partIndex === index ? Qtyref : null}
                                                />
                                            </TableCell>

                                            {/* Status */}
                                            <TableCell className={classes.tableCell}>
                                                <SelectBox
                                                    size="small"
                                                    margin="dense"
                                                    disabled={isReadOnly || (isTechnician && isRowFittedFromApi)}
                                                    List={screenDropdown.partReqStatus || []}
                                                    name="partReqStatus"
                                                    onChange={(e) => handleAssociateInput(e, row.lineID)}
                                                    value={row.partReqStatus}
                                                />
                                            </TableCell>

                                            {/* Returned? */}
                                            <TableCell className={classes.tableCell} align="center">
                                                {row.partReqID && (row.partReturnStatusID === 1 || row.partReturnStatusID === 2) && (
                                                    <SecondaryCheckbox
                                                        name="isReturnToPartsDepo"
                                                        checked={row.isReturnToPartsDepo || row.partReturnStatusID === 2}
                                                        onChange={(e) => handleAssociateInput(e, row.lineID)}
                                                        disabled={isReadOnly || row.partReturnStatusID === 2 || (isTechnician && isRowFittedFromApi)}
                                                    />
                                                )}
                                            </TableCell>

                                            {/* Actions: Sub-parts & Photos */}
                                            <TableCell className={classes.tableCell} align="right">
                                                <Box display="flex" alignItems="center" justifyContent="flex-end" gap={1}>
                                                    <Tooltip title={isReadOnly ? '' : 'Add Sub-Part'}>
                                                        <span>
                                                            <IconButton
                                                                size="small"
                                                                disabled={isReadOnly}
                                                                onClick={isReadOnly ? undefined : () => handleSubPartModal(row.lineID)}
                                                                style={{ color: isReadOnly ? '#94a3b8' : '#2563eb' }}
                                                            >
                                                                <DeviceHubIcon fontSize="small" />
                                                            </IconButton>
                                                        </span>
                                                    </Tooltip>

                                                    <Tooltip title="Upload / View Photos">
                                                        <span>
                                                            <IconButton
                                                                size="small"
                                                                onClick={handleImage(row)}
                                                                style={{ color: '#475569' }}
                                                            >
                                                                <Badge
                                                                    badgeContent={row.photosUploaded}
                                                                    color="primary"
                                                                    anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                                                                >
                                                                    <PhotoCameraIcon fontSize="small" />
                                                                </Badge>
                                                            </IconButton>
                                                        </span>
                                                    </Tooltip>
                                                </Box>
                                                {row.isImageErrorMessage && (
                                                    <FormHelperText error style={{ margin: 0, textAlign: 'right' }}>
                                                        {row.isImageErrorMessage}
                                                    </FormHelperText>
                                                )}
                                            </TableCell>
                                        </TableRow>

                                        {/* Child / Sub-Parts Nested Section */}
                                        {row.children?.length > 0 && (
                                            <TableRow key={`children_${row.lineID || index}`}>
                                                <TableCell colSpan={7} style={{ padding: '4px 16px 8px 48px', backgroundColor: '#f8fafc' }}>
                                                    <Box className={classes.nestedRowContainer}>
                                                        <Typography variant="caption" style={{ fontWeight: 700, color: '#1e40af', marginBottom: 4, display: 'block' }}>
                                                            Sub-Parts ({row.children.length})
                                                        </Typography>
                                                        <Table className={classes.nestedTable} size="small">
                                                            <TableHead>
                                                                <TableRow>
                                                                    <TableCell className={classes.nestedHeaderCell} style={{ width: '35%' }}>Description</TableCell>
                                                                    <TableCell className={classes.nestedHeaderCell} style={{ width: '25%' }}>Part No.</TableCell>
                                                                    <TableCell className={classes.nestedHeaderCell} style={{ width: '10%' }}>Qty</TableCell>
                                                                    <TableCell className={classes.nestedHeaderCell} style={{ width: '15%' }}>Status</TableCell>
                                                                    <TableCell className={classes.nestedHeaderCell} style={{ width: '10%' }}>Returned?</TableCell>
                                                                    <TableCell className={classes.nestedHeaderCell} style={{ width: '5%' }} align="right"></TableCell>
                                                                </TableRow>
                                                            </TableHead>
                                                            <TableBody>
                                                                {row.children.map((a) => {
                                                                    const storedStatusVal = screenDropdown.partReqStatus?.find((k) => k.id === a.partReqStatus)?.name || 'N/A';
                                                                    const isChildFittedFromApi = a.initialPartReqStatus !== undefined
                                                                        ? a.initialPartReqStatus === 15
                                                                        : Boolean(a.partReqID && a.partReqStatus === 15);

                                                                    return (
                                                                        <TableRow key={a.lineID || a.partNumber} hover>
                                                                            <TableCell className={classes.nestedCell}>{a.partDescription || '-'}</TableCell>
                                                                            <TableCell className={classes.nestedCell}>{a.partNumber || '-'}</TableCell>
                                                                            <TableCell className={classes.nestedCell}>{a.partQty || 0}</TableCell>
                                                                            <TableCell className={classes.nestedCell}>{storedStatusVal}</TableCell>
                                                                            <TableCell className={classes.nestedCell}>{a.isReturnToPartsDepo ? 'Yes' : 'No'}</TableCell>
                                                                            <TableCell className={classes.nestedCell} align="right">
                                                                                <Box display="flex" alignItems="center" justifyContent="flex-end" gap={0.5}>
                                                                                    <Tooltip title={isReadOnly || (isTechnician && isChildFittedFromApi) ? '' : 'Edit Sub-Part'}>
                                                                                        <span>
                                                                                            <IconButton
                                                                                                size="small"
                                                                                                disabled={isReadOnly || (isTechnician && isChildFittedFromApi)}
                                                                                                onClick={() => handleEditSubPart(a)}
                                                                                                style={{ color: isReadOnly || (isTechnician && isChildFittedFromApi) ? '#cbd5e1' : '#3b82f6', padding: 2 }}
                                                                                            >
                                                                                                <EditIcon style={{ fontSize: 18 }} />
                                                                                            </IconButton>
                                                                                        </span>
                                                                                    </Tooltip>

                                                                                    <Tooltip title={isReadOnly || (isTechnician && isChildFittedFromApi) ? '' : 'Remove Sub-Part'}>
                                                                                        <span>
                                                                                            <IconButton
                                                                                                size="small"
                                                                                                disabled={isReadOnly || (isTechnician && isChildFittedFromApi)}
                                                                                                onClick={() => removeAssociatedRow(a.lineID)}
                                                                                                style={{ color: isReadOnly || (isTechnician && isChildFittedFromApi) ? '#cbd5e1' : '#ef4444', padding: 2 }}
                                                                                            >
                                                                                                <RemoveCircleOutlineIcon style={{ fontSize: 18 }} />
                                                                                            </IconButton>
                                                                                        </span>
                                                                                    </Tooltip>

                                                                                    <Tooltip title="Photo">
                                                                                        <IconButton
                                                                                            size="small"
                                                                                            onClick={handleImage(a)}
                                                                                            style={{ padding: 2, color: '#475569' }}
                                                                                        >
                                                                                            <Badge variant="dot" color="primary">
                                                                                                <PhotoCameraIcon style={{ fontSize: 18 }} />
                                                                                            </Badge>
                                                                                        </IconButton>
                                                                                    </Tooltip>
                                                                                </Box>
                                                                                {a.isImageErrorMessage && (
                                                                                    <FormHelperText error style={{ margin: 0, fontSize: '0.7rem' }}>
                                                                                        {a.isImageErrorMessage}
                                                                                    </FormHelperText>
                                                                                )}
                                                                            </TableCell>
                                                                        </TableRow>
                                                                    );
                                                                })}
                                                            </TableBody>
                                                        </Table>
                                                    </Box>
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </React.Fragment>
                                ) : null;
                            })
                        ) : (
                            <TableRow>
                                <TableCell colSpan={7} align="center" style={{ padding: 36 }}>
                                    <Box className={classes.centerBox}>
                                        <Typography variant="body1" color="textSecondary" style={{ fontWeight: 500 }}>
                                            No Associated Parts Added Yet
                                        </Typography>
                                        <Typography variant="caption" color="textSecondary">
                                            Click the plus (+) icon in the header or button below to add parts.
                                        </Typography>
                                        {!isReadOnly && (
                                            <Box mt={1}>
                                                <Button
                                                    variant="outlined"
                                                    color="primary"
                                                    size="small"
                                                    startIcon={<AddIcon />}
                                                    onClick={handleAssociatedRow}
                                                    style={{ textTransform: 'none', fontWeight: 600 }}
                                                >
                                                    Add Part
                                                </Button>
                                            </Box>
                                        )}
                                    </Box>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Popovers for Search Matches */}
            <Popover
                open={Boolean(anchorE2)}
                anchorEl={anchorE2}
                onClose={handleClosePOP}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            >
                <MatchingPartList list={state.matchingPartNumber} loading={state.loading} handleRowSelect={handleRowSelect} />
            </Popover>

            <Popover
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                onClose={handleClosePOP}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            >
                <MatchingPartList list={state.matchingPartNumber} loading={state.loading} handleRowSelect={handleRowSelect} />
            </Popover>

            {/* Modal Bottom Actions */}
            <Box className={classes.footerActions}>
                <Grid container spacing={2}>
                    <Grid item xs={6}>
                        <SecondaryButton fullWidth onClick={handleSave} disabled={isReadOnly}>
                            Save
                        </SecondaryButton>
                    </Grid>
                    <Grid item xs={6}>
                        <SecondaryButton fullWidth onClick={onClose}>
                            Cancel
                        </SecondaryButton>
                    </Grid>
                </Grid>
            </Box>

            {/* Sub-Part Add/Edit Modal */}
            {state.showAddParts && (
                <DialogComp
                    title={state.selectedAssociatedSubData?.lineID ? 'Edit Sub-Part' : 'Add Sub-Part'}
                    onClose={handleCloseSubPartModal}
                    maxWidth="sm"
                    fullWidth
                >
                    <ChildAssociatedRowValue
                        handleChildAssocate={handleChildAssocate}
                        selectedAssociatedSubData={state.selectedAssociatedSubData || {}}
                        screenDropdown={screenDropdown}
                        onCancel={handleCloseSubPartModal}
                    />
                </DialogComp>
            )}

            {/* Photo Upload Modal */}
            {state.showImage && (
                <DialogComp title="Image Upload" onClose={() => onImagemodalClose(false)} fullWidth maxWidth="lg">
                    <PartReqSupplierPhotoModal
                        partId={state.partId}
                        desc={state.desc}
                        onClose={onImagemodalClose}
                        partDateCreated={state.partDateCreated}
                        disabled={isReadOnly}
                    />
                </DialogComp>
            )}
        </Box>
    );
}
